import { CustomValidator } from "../../common/validation/types";
import { isValidCuitCuil } from "../../common/validation/cuit";
import { identifierChanged, identifierFeedback } from "../../common/validation/identifierValidation";
import { PublisherValuesType } from "./PublisherCommonStore";

/**
 * Validación matemática del CUIT/CUIL (dígito verificador, módulo 11 AFIP).
 * Espejo del server (validatePublisherCuitCheckDigit): el server es la
 * autoridad, esto es UX. Solo valida si hay valor.
 */
export const publisherCuitCheckDigitValidator: CustomValidator<PublisherValuesType> = {
  name: "publisherCuitCheckDigitValidator",
  validate(values, langStore) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    if (values.taxId?.trim() && !isValidCuitCuil(values.taxId)) {
      errors.taxId = {
        message: langStore.cuitCheckDigitInvalid,
        fieldLabel: langStore.PublisherTaxIdLabel,
      };
    }

    return { errors, cardErrors };
  },
};

/**
 * Cuando la editorial es Persona Física, el formulario DNDA requiere
 * maritalStatus y nationality. Ambos campos son técnicamente opcionales en la
 * DB para no romper edits sobre datos legacy, así que aplicamos la regla acá
 * en el front. El seudónimo NO se exige: es genuinamente opcional (solo si la
 * editorial actúa bajo uno; sin él se usa la razón social).
 */
export const publisherNaturalPersonValidator: CustomValidator<PublisherValuesType> = {
  name: "publisherNaturalPersonValidator",
  validate(values, langStore) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    if (values.personType !== "naturalPerson") {
      return { errors, cardErrors };
    }

    if (!values.maritalStatus) {
      errors.maritalStatus = {
        message: langStore.publisherNaturalPersonMaritalStatusRequired,
        fieldLabel: langStore.PublisherMaritalStatusLabel,
      };
    }

    if (!values.showNationality?.id) {
      errors.nationality = {
        message: langStore.publisherNaturalPersonNationalityRequired,
        fieldLabel: langStore.PublisherNationalityLabel,
      };
    }

    return { errors, cardErrors };
  },
};

/**
 * Una editorial no puede guardarse sin al menos un representante firmante
 * (isSigner). Cuenta solo representantes vigentes (excluye los marcados para
 * baja). Espejo del server (validatePublisherHasSignerRepresentative): el
 * server es la autoridad, esto es UX.
 */
export const publisherSignerRepresentativeValidator: CustomValidator<PublisherValuesType> = {
  name: "publisherSignerRepresentativeValidator",
  validate(values, langStore) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    const liveReps = (values.showRepresentatives ?? []).filter((r) => {
      const status = (r as any).__status__;
      return status !== "deleted" && status !== "original-deleted";
    });

    if (!liveReps.some((r) => r.isSigner === true)) {
      errors.representatives = {
        message: langStore.publisherRequiresSignerRepresentative,
        fieldLabel: langStore.PublisherRepresentativesLabel,
      };
    }

    return { errors, cardErrors };
  },
};

/**
 * CAE/IPI de la editorial (un IPI Name Number): error si está mal formado, si los
 * dígitos de control no coinciden o si no está en su forma de 11 dígitos. Solo si el
 * campo cambió contra el original. Vacío no se revisa acá: el campo es obligatorio y
 * eso ya lo exige el form. Espejo del server (validateCaeIpiIfChanged). Puro: no
 * escribe en `values`.
 */
export const publisherCaeIpiValidator: CustomValidator<PublisherValuesType> = {
  name: "publisherCaeIpiValidator",
  validate(values, langStore, _cache, original) {
    const errors: Record<string, { message: string; fieldLabel: string }> = {};
    const cardErrors: Record<string, string[]> = {};

    if (identifierChanged(values.caeIpi, original, "caeIpi")) {
      const { error } = identifierFeedback("ipi", values.caeIpi, langStore.language);
      if (error) {
        errors.caeIpi = { message: error, fieldLabel: langStore.PublisherCaeIpiLabel };
      }
    }

    return { errors, cardErrors };
  },
};
